import Phaser from "phaser";
import { BaseModal } from "@phaser/ui/modals/BaseModal";
import type { MonsterCardModalPayload } from "@phaser/ui/modals/ModalManager";
import { getMonsterById } from "@/phaser/core/monsters/monsterCatalog";
import { CloseXBtn } from "@phaser/ui/btns/CloseXBtn";
import { monsterCountManager } from "@/phaser/core/monsters/MonsterCountManager";
import { t } from "@/i18n/i18n";

export class MonsterCardModal extends BaseModal {
  private panelW: number;
  private panelH: number;

  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    payload: MonsterCardModalPayload,
    onClose?: () => void,
  ) {
    super(scene, { width, height, onClose });

    this.panelW = (width * 681) / 768;
    this.panelH = (height * 883) / 1366;

    this.buildUI(width, height, payload);
    this.setDepth(100);
  }

  private buildUI(
    width: number,
    height: number,
    payload: MonsterCardModalPayload,
  ) {
    const monster = getMonsterById(payload.monsterId);
    const count = monsterCountManager.getCount(payload.monsterId);

    const appearanceUnlocked = count >= 100;
    const favoriteUnlocked = count >= 300;
    const nameUnlocked = count >= 500;
    const descUnlocked = count >= 1000;

    const appearanceValue = appearanceUnlocked
      ? t("codex.appearance_value", {
          score: monster.minSpawnScore.toLocaleString(),
        })
      : t("codex.unlock_condition", { value: 100 });

    const favoriteValue = favoriteUnlocked
      ? t(`monster.${monster.id}.favorite`)
      : t("codex.unlock_condition", { value: 300 });

    const nameValue = nameUnlocked
      ? t(`monster.${monster.id}.name`)
      : t("codex.unlock_condition", { value: 500 });

    const descValue = descUnlocked
      ? t(`monster.${monster.id}.description`)
      : t("codex.unlock_condition", { value: "1,000" });

    // 카드 본체 컨테이너
    const cardContainer = this.scene.add.container(width / 2, height / 2);

    // 몬스터 카드
    const panel = this.scene.add
      .image(0, 0, "monster-card-modal-bg")
      .setDisplaySize(this.panelW, this.panelH)
      .setOrigin(0.5);

    // 몬스터 이미지
    const monsterImg = this.scene.add
      .image(-this.panelW * 0.24, -this.panelH * 0.3, monster.textureKey)
      .setOrigin(0.5)
      .setScale(0.5);

    // 출현 조건 라벨
    const appearanceLabel = this.scene.add
      .text(
        this.panelW * 0.21,
        -this.panelH * 0.4,
        t("codex.appearance_label"),
        {
          fontFamily: "Black Han Sans",
          fontSize: "40px",
          color: "#FDD089",
          align: "center",
        },
      )
      .setOrigin(0.5);

    const appearanceLine = this.scene.add
      .rectangle(this.panelW * 0.21, -this.panelH * 0.37, 291, 4, 0xfdd089)
      .setOrigin(0.5);

    // 출현 조건
    const appearanceText = this.scene.add
      .text(this.panelW * 0.21, -this.panelH * 0.34, appearanceValue, {
        fontFamily: "Arial",
        fontSize: "32px",
        color: appearanceUnlocked ? "#FFFFFF" : "#FFD93D",
        align: "center",
        wordWrap: {
          width: 291,
        },
      })
      .setOrigin(0.5);

    // 좋아하는 것 라벨
    const favoriteLabel = this.scene.add
      .text(
        this.panelW * 0.21,
        -this.panelH * 0.26,
        t("codex.favorite_label"),
        {
          fontFamily: "Black Han Sans",
          fontSize: "40px",
          color: "#FDD089",
          align: "center",
        },
      )
      .setOrigin(0.5);

    const favoriteLine = this.scene.add
      .rectangle(this.panelW * 0.21, -this.panelH * 0.23, 291, 4, 0xfdd089)
      .setOrigin(0.5);

    // 좋아하는 것
    const favoriteText = this.scene.add
      .text(this.panelW * 0.21, -this.panelH * 0.2, favoriteValue, {
        fontFamily: "Arial",
        fontSize: "32px",
        color: favoriteUnlocked ? "#FFFFFF" : "#FFD93D",
        align: "center",
        wordWrap: {
          width: 291,
        },
      })
      .setOrigin(0.5);

    // 종족명 라벨
    const nameLabel = this.scene.add
      .text(0, -this.panelH * 0.09, t("codex.name_label"), {
        fontFamily: "Black Han Sans",
        fontSize: "40px",
        color: "#FDD089",
        align: "center",
      })
      .setOrigin(0.5);

    const nameLine = this.scene.add
      .rectangle(0, -this.panelH * 0.06, 581, 4, 0xfdd089)
      .setOrigin(0.5);

    // 종족명
    const nameText = this.scene.add
      .text(0, -this.panelH * 0.01, nameValue, {
        fontFamily: "Arial",
        fontStyle: "bold",
        fontSize: "60px",
        color: nameUnlocked ? "#FFFFFF" : "#FFD93D",
        align: "center",
        wordWrap: {
          width: 581,
        },
      })
      .setOrigin(0.5);

    // 설명 라벨
    const descLabel = this.scene.add
      .text(0, this.panelH * 0.09, t("codex.description_label"), {
        fontFamily: "Black Han Sans",
        fontSize: "40px",
        color: "#FDD089",
        align: "center",
      })
      .setOrigin(0.5);

    const descLine = this.scene.add
      .rectangle(0, this.panelH * 0.12, 581, 4, 0xfdd089)
      .setOrigin(0.5);

    // 설명
    const descText = this.scene.add
      .text(0, this.panelH * 0.14, descValue, {
        fontFamily: "Arial",
        fontSize: "32px",
        color: descUnlocked ? "#FFFFFF" : "#FFD93D",
        align: "center",
        wordWrap: { width: 529 },
      })
      .setOrigin(0.5, 0);

    // 닫기 버튼
    const closeBtn = new CloseXBtn(
      this.scene,
      this.panelW / 2 - 60,
      -this.panelH / 2 + 60,
      () => {
        this.scene.events.emit("monster-detail-closed");
        this.close();
      },
    );

    cardContainer.add([
      panel,
      appearanceLabel,
      appearanceLine,
      appearanceText,
      favoriteLabel,
      favoriteLine,
      favoriteText,
      nameLabel,
      nameLine,
      nameText,
      monsterImg,
      descLabel,
      descLine,
      descText,
      closeBtn,
    ]);

    // 등장 연출
    const startY = cardContainer.y + 500;
    const startAngle = -45;

    cardContainer.setY(startY);
    cardContainer.setScale(0.7);
    cardContainer.setAngle(startAngle);

    this.scene.tweens.add({
      targets: cardContainer,
      y: startY - 500,
      scale: 1,
      angle: 0,
      duration: 300,
      ease: "Cubic.Out",
    });

    // 누적 분류 횟수
    const countText = this.scene.add
      .text(
        width / 2,
        height / 2 + this.panelH * 0.56,
        t("codex.sorted_count", {
          value: count.toLocaleString(),
        }),
        {
          fontFamily: "Black Han Sans",
          fontSize: "50px",
          color: "#FFFFFF",
          align: "center",
          wordWrap: { width: 529 },
        },
      )
      .setOrigin(0.5);

    this.add([cardContainer, countText]);
  }
}
